import { orgTripleApi, wrapUncaughtErrors } from '@ardoq/api';
import { isArdoqError, Result } from '@ardoq/common-helpers';
import { logError } from '@ardoq/logging';
import { defer, filter, merge, scan, tap } from 'rxjs';
import { websocket$ } from '../sync/websocket$';
import { ardoqEventOperations } from '../sync/ardoqEventOperations';
import { APIOrgTripleAttributes } from '@ardoq/api-types';
import { ArdoqEvent, EventType } from '../sync/types';

const orgTripleEvent$ = websocket$.pipe(
  filter(ardoqEventOperations.isOrgTripleEvent)
);

type InitialOrgTriplesLoad = {
  'event-type': 'initial-load';
  orgTriples: Result<APIOrgTripleAttributes[]>;
};

const isInitialLoad = (
  event: ArdoqEvent<unknown> | InitialOrgTriplesLoad
): event is InitialOrgTriplesLoad => event['event-type'] === 'initial-load';

const fetchOrgTriples = async (): Promise<InitialOrgTriplesLoad> => {
  const orgTriples = await orgTripleApi.fetchAll();
  return {
    ['event-type']: 'initial-load',
    orgTriples,
  };
};

const applyUpdate = (
  orgTriples: APIOrgTripleAttributes[],
  event: ArdoqEvent<APIOrgTripleAttributes>
): APIOrgTripleAttributes[] => {
  switch (event['event-type']) {
    case EventType.CREATE:
      return [...orgTriples, event.data];
    case EventType.UPDATE:
      return orgTriples.map(ot =>
        ot._id === event.data._id ? event.data : ot
      );
    case EventType.DELETE:
      return orgTriples.filter(ot => ot._id !== event.data._id);
    default:
      return orgTriples;
  }
};

/**
 * A cold observable that fetches org triples when subscribed to.
 *
 * If an initial emit is needed (to show a loading state), add `startWith` on the
 * call site.
 *
 * If the request errors, an `ArdoqError` is emitted. The error is automatically
 * logged.
 */
export const orgTriples$ = defer(() =>
  merge(fetchOrgTriples(), orgTripleEvent$)
).pipe(
  scan(
    (
      orgTriples: Result<APIOrgTripleAttributes[]>,
      event: ArdoqEvent<APIOrgTripleAttributes> | InitialOrgTriplesLoad
    ): Result<APIOrgTripleAttributes[]> => {
      if (isInitialLoad(event)) {
        return event.orgTriples;
      }
      if (isArdoqError(orgTriples)) {
        return orgTriples;
      }
      return applyUpdate(orgTriples, event);
    },
    [] as Result<APIOrgTripleAttributes[]>
  ),
  wrapUncaughtErrors(),
  tap(value => {
    if (isArdoqError(value)) {
      logError(value);
    }
  })
);
